import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Inmueble, PropietarioInmueble, InmuebleEstado, DestinoInmueble } from '@/types';
import inmuebleService from '@/services/inmuebleService';
import Input from '@/components/common/Input';
import Button from '@/components/common/Button';
import PropietarioManager from '@/components/property/PropietarioManager';
import MainLayout from '@/components/layout/MainLayout';
import { useDialog } from '@/hooks/useDialog';

export default function PropertyForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const [form, setForm] = useState<Partial<Inmueble>>({ direccion: '', ciudad: '', estado: InmuebleEstado.LIBRE, destino_inmueble: DestinoInmueble.PARTICULAR });
  const [propietarios, setPropietarios] = useState<PropietarioInmueble[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      loadProperty(Number(id));
    }
  }, [id]);

  const loadProperty = async (propertyId: number) => {
    setLoading(true);
    try {
      const res = await inmuebleService.getInmuebleById(propertyId);
      const property = (res as any).data || res;
      setForm(property);

      // Set propietarios from the property data
      if (property.propietarios && Array.isArray(property.propietarios)) {
        setPropietarios(property.propietarios);
      }
    } catch (error) {
      console.error('Error loading property:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar el inmueble',
      });
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k: keyof Inmueble, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async () => {
    // Validate required fields
    if (propietarios.length === 0) {
      showAlert({
        type: 'warning',
        title: 'Validación',
        message: 'Debe seleccionar al menos un propietario para el inmueble',
      });
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        propietarios: propietarios.map(p => ({
          id: p.id,
          porcentaje_propiedad: p.porcentaje_propiedad || 100,
          no_comision: p.no_comision || false,
          no_irpf: p.no_irpf || false,
        })),
      };

      console.log('Sending data:', data);

      if (form.id) await inmuebleService.updateInmueble(form.id, data);
      else await inmuebleService.createInmueble(data);
      navigate('/properties');
    } catch (error: any) {
      console.error('Error saving inmueble:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al guardar el inmueble';
      const validationErrors = error?.response?.data?.errors;

      if (validationErrors) {
        console.error('Validation errors:', validationErrors);
        const errorList = Object.entries(validationErrors).map(([key, value]) => `${key}: ${value}`).join('\n');
        showAlert({
          type: 'error',
          title: 'Error de validación',
          message: errorList,
        });
      } else {
        showAlert({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-4 max-w-2xl">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{id ? 'Editar' : 'Crear'} Inmueble</h1>
          <p className="text-gray-600">
            {id ? 'Actualiza la información del inmueble' : 'Complete los datos del nuevo inmueble'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Información Básica</h2>
              <div className="space-y-4">
                <Input
                  label="Dirección *"
                  value={form.direccion || ''}
                  onChange={(e) => onChange('direccion', e.target.value)}
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="No. Padrón"
                    value={form.padron || ''}
                    onChange={(e) => onChange('padron', e.target.value)}
                    placeholder="Ej: 12345"
                  />
                  <Input
                    label="Ciudad *"
                    value={form.ciudad || ''}
                    onChange={(e) => onChange('ciudad', e.target.value)}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Destino *</label>
                    <select
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.destino_inmueble || DestinoInmueble.PARTICULAR}
                      onChange={(e) => onChange('destino_inmueble', e.target.value)}
                    >
                      <option value={DestinoInmueble.COMERCIAL}>Comercial</option>
                      <option value={DestinoInmueble.PARTICULAR}>Particular</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado *</label>
                    <select
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.estado}
                      onChange={(e) => onChange('estado', e.target.value)}
                    >
                      <option value={InmuebleEstado.LIBRE}>Libre</option>
                      <option value={InmuebleEstado.ALQUILADO}>Alquilado</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    value={form.descripcion || ''}
                    onChange={(e) => onChange('descripcion', e.target.value)}
                    placeholder="Detalles adicionales sobre el inmueble (servicios, amenities, condiciones especiales, etc.)"
                  />
                </div>
              </div>
            </div>

            {/* Client Associations */}
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Propietarios *</h2>
              <p className="text-sm text-gray-600 mb-4">
                Asigne uno o más propietarios a este inmueble. Puede especificar el porcentaje de propiedad
                y configurar opciones fiscales para cada propietario.
              </p>

              <PropietarioManager
                propietarios={propietarios}
                onPropietariosChange={setPropietarios}
                required={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Nota:</strong> Para asociar inquilinos, debe crear un contrato desde la sección de Contratos.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="border-t pt-6 flex gap-3">
              <Button onClick={onSubmit} disabled={saving}>
                {saving ? 'Guardando...' : id ? 'Actualizar Inmueble' : 'Crear Inmueble'}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/properties')}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

